import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import type { LineEndT } from "tweenly"
import { LINE_ENDS, LINE_HEIGHT, renderLineSvg } from "tweenly"

const LINE_END_LABELS: Record<LineEndT, string> = {
    none: "None",
    "line-arrow": "Line arrow",
    "triangle-arrow": "Triangle arrow",
    "reversed-triangle": "Reversed triangle",
    circle: "Circle",
    diamond: "Diamond",
}

/** A short line drawn with the real renderer, so the preview is exactly what the canvas shows. */
const LineEndPreview = ({ end, which, idPrefix, width = 56 }: PreviewProps) => (
    <div
        style={{
            width,
            height: LINE_HEIGHT,
            margin: "0 8px",
            color: "currentColor",
            ["--line-weight" as string]: "1.5px",
        }}
        dangerouslySetInnerHTML={{
            __html: renderLineSvg({
                id: `${idPrefix}-${which}-${end}`,
                [which === "start" ? "startPoint" : "endPoint"]: end,
            }),
        }}
    />
)

type PreviewProps = {
    end: LineEndT
    which: "start" | "end"
    idPrefix: string
    width?: number | string
}

interface Props {
    which: "start" | "end"
    value: LineEndT | undefined
    onChange: (value: LineEndT) => void
}

/** Figma's start / end point picker: every end type with a preview of it. */
const LineEndSelect = ({ which, value, onChange }: Props) => (
    <FormControl>
        <InputLabel>{which === "start" ? "start point" : "end point"}</InputLabel>
        <Select
            value={value ?? "none"}
            label={which === "start" ? "start point" : "end point"}
            onChange={(e: SelectChangeEvent) => onChange(e.target.value as LineEndT)}
            renderValue={(selected) => (
                <LineEndPreview
                    end={selected as LineEndT}
                    which={which}
                    idPrefix="line-end-value"
                    // the chosen end fills the field, the way Figma previews it
                    width="calc(100% - 16px)"
                />
            )}
        >
            {LINE_ENDS.map((end) => (
                <MenuItem key={end} value={end} title={LINE_END_LABELS[end]}>
                    <LineEndPreview end={end} which={which} idPrefix="line-end-option" />
                    {LINE_END_LABELS[end]}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
)

export default LineEndSelect
